import React, { useState, useMemo } from "react";
import { useVisitManagementStore } from "../../../store/visitStore";
import { useEmployeeStore } from "../../../store/employeeStore";
import {
    Search, Plus, Trash2, MapPin,
    CalendarDays, Clock, GripVertical, Edit, UserCircle2
} from "lucide-react";
import { InputText } from "primereact/inputtext";
import { IconTextButton } from "../../../components/ButtonComponents";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { toast } from "react-toastify";
import { VisitTemplateDialog } from "./VisitTemplateDialog";
import { VisitTemplate } from "../../../types/visit";
import { Employee } from "../../../types/employee";

// Dnd Kit Imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    defaultDropAnimationSideEffects,
    useDroppable
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DAYS = [
    { id: 1, label: "Pazartesi" },
    { id: 2, label: "Salı" },
    { id: 3, label: "Çarşamba" },
    { id: 4, label: "Perşembe" },
    { id: 5, label: "Cuma" },
    { id: 6, label: "Cumartesi" },
    { id: 7, label: "Pazar" }
];

// --- Droppable Column for Empty State Support ---
const DroppableColumn = ({ id, children }: { id: string, children: React.ReactNode }) => {
    const { isOver, setNodeRef } = useDroppable({ id });
    return (
        <div
            ref={setNodeRef}
            className={`p-2 rounded-2xl min-h-[500px] space-y-2 flex flex-col transition-all duration-200 border-2 ${isOver ? 'bg-primary/5 border-primary/30 border-dashed' : 'bg-bgAlt border-transparent'
                }`}
        >
            {children}
        </div>
    );
};

// --- Sortable Item Component ---
interface SortableVisitCardProps {
    item: VisitTemplate;
    onDelete: (id: number) => void;
    onEdit: (item: VisitTemplate) => void;
}

const SortableVisitCard = ({ item, onDelete, onEdit }: SortableVisitCardProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`p-3 rounded-xl border ${isDragging ? 'border-primary shadow-xl scale-[1.02]' : 'border-borderColor shadow-sm'} group hover:border-primary/40 hover:shadow-md transition-all bg-card text-text relative flex gap-2 w-full`}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-text/40 hover:text-primary mt-1 shrink-0 p-1 -ml-1 rounded-md hover:bg-bgAlt"
            >
                <GripVertical size={16} />
            </div>

            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2 min-w-0">
                        <div className="w-6 h-6 mt-0.5 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-[10px] font-black shrink-0">
                            {item.visitOrder}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-bold text-text truncate leading-tight" title={item.account?.name}>
                                {item.account?.name}
                            </div>
                            <div className="text-[10px] text-text/60 mt-1 flex items-center gap-1">
                                <MapPin size={10} className="shrink-0" />
                                <span className="truncate" title={item.address?.title}>{item.address?.title || "Merkez"}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                        <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => onEdit(item)}
                            className="text-text/40 hover:text-blue-500 p-1 rounded-md hover:bg-bgAlt"
                            title="Düzenle"
                        >
                            <Edit size={14} />
                        </button>
                        <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => onDelete(item.id)}
                            className="text-text/40 hover:text-danger p-1 rounded-md hover:bg-bgAlt"
                            title="Sil"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>

                {(item.interval > 1 || item.frequency === 'MONTHLY') && (
                    <div className="mt-2    flex items-center gap-1">
                        <Clock size={10} className="text-amber-500" />
                        <span className="text-[9px] font-bold text-amber-600 uppercase">
                            {item.frequency === 'WEEKLY'
                                ? `${item.interval} Haftada Bir`
                                : `Ayda Bir (${item.dayOfMonth}. Gün)`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export const VisitPlanner = () => {
    const { templates: templateStore } = useVisitManagementStore();
    const employeeStore = useEmployeeStore();
    const { confirm, modal: confirmModal } = useConfirmModal();
    const employeeSelect = useSelectDialog<Employee>();

    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [itemToEdit, setItemToEdit] = useState<VisitTemplate | null>(null);
    const [templates, setTemplates] = useState<VisitTemplate[]>([]);
    const [loading, setLoading] = useState(false);

    const [activeId, setActiveId] = useState<number | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchTemplates = async (empId: number) => {
        setLoading(true);
        try {
            const res = await templateStore.fetchDataDynamic({
                pageSize: 1000,
                filter: {
                    logic: "and",
                    filters: [{ field: "employeeId", operator: "eq", value: empId }]
                },
                sort: [{ field: "visitOrder", dir: "asc" }]
            });
            setTemplates(res.items || []);
        } catch (error) {
            toast.error("Plan yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const openEmployeeSelect = async () => {
        const res = await employeeStore.fetchDataDynamic({ pageIndex: 1, pageSize: 10 });
        const selected = await employeeSelect.open({
            title: "Plasiyer Seçin",
            data: res?.items || [],
            searchFields: [
                { field: "firstName", operator: "contains" },
                { field: "lastName", operator: "contains" }
            ],
            columns: [
                { field: "firstName", header: "Ad" },
                { field: "lastName", header: "Soyad" }
            ],
            onFilter: async (p) => {
                const r = await employeeStore.fetchDataDynamic({
                    pageIndex: p.pageIndex,
                    pageSize: p.pageSize,
                    filter: p.filter
                });
                return { data: r.items, total: r.total };
            }
        });

        if (selected) {
            setSelectedEmployee(selected);
            fetchTemplates(selected.id);
        }
    };

    const handleSaveRoute = async (data: Partial<VisitTemplate>) => {
        try {
            if (data.id) {
                await templateStore.updateItem(data.id, data as any);
                toast.success("Rota başarıyla güncellendi");
            } else {
                await templateStore.createItem(data as any);
                toast.success("Rota başarıyla eklendi");
            }
            if (selectedEmployee) fetchTemplates(selectedEmployee.id);
            setIsDialogVisible(false);
            setItemToEdit(null);
        } catch (err) {
            toast.error("Kaydedilemedi");
        }
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm("Bu ziyareti rotadan çıkarmak istediğinize emin misiniz?");
        if (ok && selectedEmployee) {
            await templateStore.deleteItem(id);
            toast.success("Rota silindi");
            fetchTemplates(selectedEmployee.id);
        }
    };

    const handleEdit = (item: VisitTemplate) => {
        setItemToEdit(item);
        setIsDialogVisible(true);
    };

    // Drag events
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as number);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;

        if (!over) return;

        const activeItem = templates.find(t => t.id === active.id);
        if (!activeItem) return;

        let newDayId = activeItem.dayOfWeek;
        let newOrder = activeItem.visitOrder;

        const overId = over.id;

        if (typeof overId === 'string' && overId.startsWith('day-')) {
            newDayId = parseInt(overId.split('-')[1], 10);
            const dayItems = templates.filter(t => t.dayOfWeek === newDayId);
            newOrder = dayItems.length > 0 ? Math.max(...dayItems.map(d => d.visitOrder)) + 1 : 1;
        } else {
            const overItem = templates.find(t => t.id === overId);
            if (overItem) {
                newDayId = overItem.dayOfWeek;
                newOrder = overItem.visitOrder;
            }
        }

        if (activeItem.dayOfWeek === newDayId && activeItem.visitOrder === newOrder) {
            return;
        }

        const updatedTemplates = templates.map(t => {
            if (t.id === active.id) {
                return { ...t, dayOfWeek: newDayId, visitOrder: newOrder };
            }
            if (t.dayOfWeek === newDayId && t.id !== active.id) {
                if (t.visitOrder >= newOrder) {
                    return { ...t, visitOrder: t.visitOrder + 1 };
                }
            }
            return t;
        });

        const finalTemplates = updatedTemplates.map(t => t);

        DAYS.forEach(day => {
            const items = finalTemplates.filter(t => t.dayOfWeek === day.id).sort((a, b) => a.visitOrder - b.visitOrder);
            items.forEach((item, index) => {
                item.visitOrder = index + 1;
            });
        });

        setTemplates(finalTemplates);

        try {
            await templateStore.updateItem(active.id as number, {
                dayOfWeek: newDayId,
                visitOrder: newOrder
            });
            if (selectedEmployee) fetchTemplates(selectedEmployee.id);
        } catch (error) {
            toast.error("Sıralama kaydedilemedi.");
            if (selectedEmployee) fetchTemplates(selectedEmployee.id);
        }
    };

    const getDayTemplates = (dayId: number) =>
        templates.filter(t => t.dayOfWeek === dayId)
            .sort((a, b) => a.visitOrder - b.visitOrder);

    const activeItem = useMemo(() => templates.find(t => t.id === activeId), [activeId, templates]);

    return (
        <div className="space-y-6 p-2 md:p-4">
            {confirmModal}

            <div className="flex flex-col md:flex-row justify-between items-start md:items-end p-5 shadow-sm bg-card gap-4 rounded-2xl border border-borderColor text-text">
                <div className="w-full md:w-1/2 lg:w-1/3">
                    <CustomSelectInput
                        label={<><UserCircle2 size={14} className="text-primary" /> Aktif Planlama Sorumlusu</>}
                        value={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : ""}
                        placeholder="Bir plasiyer seçerek başlayın..."
                        icon={UserCircle2}
                        onClick={openEmployeeSelect}
                        onClear={() => setSelectedEmployee(null)}
                    />
                </div>

                <div className="flex gap-2 w-full md:w-auto">
                    <IconTextButton
                        icon={Plus}
                        disabled={!selectedEmployee}
                        onClick={() => {
                            setItemToEdit(null);
                            setIsDialogVisible(true);
                        }}
                        className="bg-primary hover:bg-primaryHover text-white w-full md:w-auto justify-center disabled:opacity-50"
                    >
                        Yeni Müşteri Ekle
                    </IconTextButton>
                </div>
            </div>

            {selectedEmployee ? (
                <div className="flex flex-col gap-6 overflow-x-auto pb-4 custom-scrollbar w-full">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        {/* Responsive Grid instead of forced min-width */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-7 gap-4 min-w-full">
                            {DAYS.map(day => {
                                const dayItems = getDayTemplates(day.id);
                                return (
                                    <div key={day.id} className="flex flex-col gap-3">
                                        <div className="flex items-center justify-between px-2">
                                            <h3 className="text-[12px] font-black uppercase tracking-wider text-text">
                                                {day.label}
                                            </h3>
                                            <span className="bg-bgAlt text-text/80 text-[10px] px-2 py-0.5 rounded-full font-bold border border-borderColor">
                                                {dayItems.length} Durak
                                            </span>
                                        </div>

                                        <SortableContext
                                            id={`day-${day.id}`}
                                            items={dayItems.map(i => i.id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <DroppableColumn id={`day-${day.id}`}>
                                                {dayItems.length > 0 ? (
                                                    dayItems.map((item) => (
                                                        <SortableVisitCard
                                                            key={item.id}
                                                            item={item}
                                                            onDelete={handleDelete}
                                                            onEdit={handleEdit}
                                                        />
                                                    ))
                                                ) : (
                                                    <div className="flex-1 flex flex-col items-center justify-center  rounded-xl bg-transparent text-text/40 min-h-[100px]">
                                                        <CalendarDays size={20} className="mb-2 opacity-40" />
                                                        <span className="text-[10px] font-medium italic">Sürükle veya Ekle</span>
                                                    </div>
                                                )}
                                            </DroppableColumn>
                                        </SortableContext>
                                    </div>
                                );
                            })}
                        </div>

                        <DragOverlay dropAnimation={{
                            sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.4' } } }),
                        }}>
                            {activeItem ? (
                                <SortableVisitCard item={activeItem} onDelete={() => { }} onEdit={() => { }} />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 md:py-40 bg-card shadow-sm rounded-2xl border border-borderColor text-text">
                    <div className="w-20 h-20 bg-bgAlt rounded-full flex items-center justify-center mb-6">
                        <CalendarDays size={40} className="text-text/40" />
                    </div>
                    <h3 className="text-xl font-black">Planlama Modülü</h3>
                    <p className="opacity-60 mt-2 max-w-xs text-center text-sm">
                        Personelinizin haftalık rutinlerini yönetmek için yukarıdan bir seçim yapın.
                    </p>
                </div>
            )}

            <VisitTemplateDialog
                isOpen={isDialogVisible}
                onClose={() => {
                    setIsDialogVisible(false);
                    setItemToEdit(null);
                }}
                employeeId={selectedEmployee ? selectedEmployee.id : null}
                initialData={itemToEdit}
                onSave={handleSaveRoute}
            />
            {employeeSelect.modal}
        </div>
    );
};